import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { Button, Divider } from "@fluentui/react-components";
import { ArrowLeftFilled } from "@fluentui/react-icons";
import { DeleteIcon, SaveIcon } from "@fluentui/react-icons-mdl2";
import { formatDate } from "date-fns";
import { Form, Formik } from "formik";
import { useAtomValue } from "jotai";
import { ETaxonomy, IDependent, IEmployee } from "library/types";
import { formatDateFieldForPayload, formatSelectFieldForPayload } from "library/utils";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { selectedClientAtom } from "store";
import styled from "styled-components";
import * as yup from "yup";

import { AppLogoSpinner } from "components/AppLogoSpinner/AppLogoSpinner";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import SelectField from "components/controls/SelectField";
import { CollapseWrapper } from "components/effects/CollapseWrapper";
import { PrimaryButton } from "components/PrimaryButton/PrimaryButton";
import { Flex, Grid, RemoveButton } from "components/styled";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";
import { useCreateDependent } from "state/useCreateDependent";
import { useDeleteDependent } from "state/useDeleteDependent";
import { useUpdateDependent } from "state/useUpdateDependent";

import { BasicFields } from "../BasicFields";
import { MainFields } from "../MainFields";

type Props = {
  removeNewDependent?: () => void;
  dependentData?: IDependent;
  addNew?: boolean;
  employeeData?: IEmployee;
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
};
export function DependentForm({
  removeNewDependent,
  dependentData,
  employeeData,
  addNew,
  editMode,
  setEditMode,
}: Props) {
  const { t } = useTranslation();
  const dependentTypeOptions = useTaxonomyOptions(ETaxonomy.DependentType);
  const selectedCompany = useAtomValue(selectedClientAtom);

  const genderOptions = useTaxonomyOptions(ETaxonomy.Gender);
  const countryOptions = useTaxonomyOptions(ETaxonomy.Country);

  const { mutate: onUpdateDependent, isLoading: updateInProgress } = useUpdateDependent(false);

  const { mutate: onCreateDependent, isLoading: createInProgress } = useCreateDependent(false);

  const { mutate: onDeleteDependent, isLoading: deleteInProgress } = useDeleteDependent(false);

  const [deleteDependentConfirmationOpen, setDeleteDependentConfirmationOpen] = useState(false);

  const toggleDeleteConfirmationState = useCallback(() => {
    setDeleteDependentConfirmationOpen((prev) => !prev);
  }, []);

  const initDependentType = dependentTypeOptions.find(
    (option) => option.value === dependentData?.dependentType
  );

  const initialValues = useMemo(() => {
    if (!dependentData) return {};
    return {
      ...dependentData,
      dependentType: initDependentType,
      dateOfBirth: new Date(dependentData?.dateOfBirth || ""),

      //basic
      genderCodeId: genderOptions.find(
        (genderCode) => genderCode.value === dependentData?.genderCodeId
      ),
      citizenshipCodeId: countryOptions.find(
        (item) => item.value === dependentData?.citizenshipCodeId
      ),
      dependentCoverPhoto: dependentData?.backgroundPicturePath
        ? {
            name: dependentData?.backgroundPictureName,
            path: dependentData?.backgroundPicturePath,
            base64String: dependentData?.backgroundPictureContent,
            type: dependentData?.backgroundPictureMimeType,
          }
        : null,
      employeeDependentTag: dependentData?.employeeDependentTag,
    };
  }, [dependentData]);

  const validationSchema = yup.object().shape({
    dependentType: yup.mixed().required(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    dateOfBirth: yup.mixed().required(),
    // emailAddress: yup.string().required(),
  });

  const handleDeleteDependent = () => {
    if (!dependentData) return;
    onDeleteDependent(
      {
        countryCode: Number(selectedCompany?.countryCode),
        clientInternalNumberGOS: Number(selectedCompany?.clientInternalNumberGos),
        employeeDependentTag: Number(dependentData?.employeeDependentTag),
      },
      {
        onSuccess: () => {
          toast.success(t("hbh.dependentDeletedSuccessfully.label"));
          setEditMode(false);
        },
      }
    );
  };

  const handleSubmit = (values) => {
    const payload = {
      ...dependentData,
      ...values,
      employeeLoginEmailAddress: employeeData?.employeeLoginEmailAddress,
      dependentType: formatSelectFieldForPayload({
        values,
        field: "dependentType",
      }),
      dateOfBirth: formatDate(values.dateOfBirth, "yyyy-MM-dd"),
      genderCodeId: formatSelectFieldForPayload({
        values,
        field: "genderCodeId",
      }),
      citizenshipCodeId: formatSelectFieldForPayload({
        values,
        field: "citizenshipCodeId",
      }),
      ...(values.dateOfBirth ? { dateOfBirth: formatDateFieldForPayload(values.dateOfBirth) } : {}),
      dateOfExit: formatDateFieldForPayload(values.dateOfExit),

      backgroundPictureName: values?.dependentCoverPhoto?.name,
      backgroundPictureContent: values?.dependentCoverPhoto?.base64String,
      backgroundPictureMimeType: values?.dependentCoverPhoto?.type,
      ...(!values?.dependentCoverPhoto?.base64String && {
        backgroundPicturePath: values?.dependentCoverPhoto?.path,
      }),
      rowVersion: employeeData?.rowVersion,
    };

    if (dependentData) {
      onUpdateDependent(
        {
          countryCode: Number(selectedCompany?.countryCode),
          clientInternalNumberGOS: Number(selectedCompany?.clientInternalNumberGos),

          employeeDependentTag: Number(dependentData?.employeeDependentTag),
          payload: payload,
        },
        {
          onSuccess: () => {
            toast.success(t("hbh.dependentUpdatedSuccessfully.label"));
          },
        }
      );
    } else {
      onCreateDependent(
        {
          countryCode: Number(selectedCompany?.countryCode),
          clientInternalNumberGOS: Number(selectedCompany?.clientInternalNumberGos),
          payload,
        },
        {
          onSuccess: () => {
            toast.success(t("hbh.dependentAddedSuccessfully.label"));
            if (removeNewDependent) {
              removeNewDependent();
            }
            setEditMode(false);
          },
        }
      );
    }
  };

  useEffect(() => {
    if (addNew) {
      setEditMode(true);
    }
  }, [addNew]);

  const isLoading = createInProgress || updateInProgress || deleteInProgress;

  return (
    <>
      <CollapseWrapper visible={!!editMode}>
        <Container>
          <Formik
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={(values, actions) => {
              handleSubmit(values);
              actions.setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              dirty,

              setFieldValue,
              /* and other goodies */
            }) => {
              return (
                <>
                  {isLoading && <AppLogoSpinner />}
                  <Form>
                    <Grid>
                      <SelectField
                        data-help="profile-dependent-type"
                        required
                        error={errors["dependentType"] as string}
                        setFieldValue={setFieldValue}
                        value={values["dependentType"]}
                        label="Dependent"
                        options={dependentTypeOptions}
                        name={"dependentType"}
                        showEmptyOption
                        appearance="filled-lighter"
                      />
                    </Grid>
                    <MainFields appearance="filled-lighter" isDependent={true} />
                    <BasicFields appearance="filled-lighter" isDependent={true} />
                    <Flex wrap="nowrap" $justify="space-between">
                      {!addNew && (
                        <RemoveBtnWrap>
                          <RemoveButton
                            data-help="profile-dependent-delete"
                            disabled={isLoading}
                            icon={<DeleteIcon />}
                            onClick={() => {
                              setDeleteDependentConfirmationOpen(true);
                            }}
                          >
                            {addNew ? t("greco.cancel") : t("greco.delete")}
                          </RemoveButton>
                        </RemoveBtnWrap>
                      )}
                      <Flex
                        style={{
                          marginLeft: "auto",
                        }}
                        wrap="nowrap"
                      >
                        <Button
                          data-help="profile-dependent-cancel"
                          icon={<ArrowLeftFilled />}
                          onClick={() => {
                            setEditMode(false);
                            if (removeNewDependent) {
                              removeNewDependent();
                            }
                          }}
                        >
                          {t("greco.cancel")}
                        </Button>
                        <PrimaryButton
                          data-help="profile-dependent-save"
                          disabled={!dirty || isLoading}
                          icon={<SaveIcon />}
                          style={{
                            paddingLeft: "25px",
                            paddingRight: "25px",
                          }}
                        >
                          {t("greco.save")}
                        </PrimaryButton>
                      </Flex>
                    </Flex>
                  </Form>
                </>
              );
            }}
          </Formik>
          <Divider
            appearance="strong"
            style={{
              marginTop: "30px",
            }}
          />
        </Container>
      </CollapseWrapper>

      {deleteDependentConfirmationOpen && dependentData && (
        <ConfirmationDialog
          isOpen={deleteDependentConfirmationOpen}
          toggleOpen={toggleDeleteConfirmationState}
          onConfirm={handleDeleteDependent}
          description={t("greco.dialog.deleteTitle", {
            object: `${dependentData?.firstName} ${dependentData?.lastName}`,
          })}
          confirmLabel={t("greco.delete")}
          title={`${t("hbh.deleteDependent.label")} ${
            dependentData?.firstName
          } ${dependentData?.lastName}`}
        />
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
  box-sizing: border-box;
  padding: 10px;
`;

const RemoveBtnWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  button {
    width: 100%;
  }
`;
